import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import StorageIcon from '@mui/icons-material/Storage';
import FavoriteButton from '../../../../components/olymp/FavoriteButton';
import ListButton from '../../../../components/olymp/ListButton';
import SensitivityBadge from '../../../../components/olymp/SensitivityBadge';
import FrequencyCell from '../../../../components/olymp/FrequencyCell';
import ExpandedRow from './ExpandedRow';
import {
  getPrimaryName as getDefaultPrimaryName,
  getSecondaryName as getDefaultSecondaryName,
  getOMOPStatusColor,
  getOMOPStatusLabel,
  getMappingProvenance,
  getMappingProvenanceLabel,
  getMappingProvenanceColor,
  OMOP_MAPPING_SOURCE_AI,
  getOmopAbbildbarkeit,
  getOpenEhrPrimaryName,
  getAqlPfade,
  getOmopFragmentierung,
  zeigtOmop,
  zeigtOpenEhr,
  ANSICHT_ALLE,
} from '../utils/helpers';
import { getParameter, getParameterLookupId } from '../../../../services/catalogService';
import { useTranslation } from 'react-i18next';

/**
 * Parameter Row Component
 * Renders a single parameter row in the data catalog table
 *
 * @param {Object} param - Parameter data
 * @param {string} nameView - Ansicht: 'charite' | 'omop' | 'openehr' | 'alle' (#782)
 * @param {boolean} isExpanded - Whether row is expanded
 * @param {Function} onRowClick - Handler for row click
 * @param {Function} onFavoriteToggle - Handler for favorite toggle
 * @param {Array} activeWorkingList - Active working list IDs
 * @param {Array} parameterLists - All parameter lists
 * @param {Function} getParameterListStatus - Get list status for parameter
 * @param {Function} isParameterInAnyList - Check if parameter is in any list
 * @param {Function} handleRemoveFromList - Remove parameter from list
 * @param {Function} handleAddToList - Add parameter to list
 * @param {Function} handleOpenListDialog - Open list dialog
 * @param {Array} allTagsWithStats - All tags with statistics
 * @param {Function} getIconComponent - Get icon component by name
 * @param {Function} getPrimaryDescription - Get primary description
 * @param {Object} subtlePulse - Animation for deletion requests
 */
/**
 * Die Zuordnungszelle in Stellung openEHR (#772 AK2, AK3).
 *
 * **Die Kennzeichnung ist der Inhalt, nicht die Verzierung.** Ein Parameter,
 * den openEHR fuehrt und OMOP nicht, ist der Fall, um den es im Vortrag geht.
 * Er darf hier nicht als leeres Feld erscheinen: leer heisst "wir wissen es
 * nicht", die Kennzeichnung heisst "es geht nicht". Deshalb steht bei
 * `nicht_abbildbar` ein eigener Chip und nicht die Abwesenheit eines Chips.
 */
export const OpenEhrZelle = ({ param, t }) => {
  const abbildbarkeit = getOmopAbbildbarkeit(param);
  const archetyp = getOpenEhrPrimaryName(param);
  const pfade = getAqlPfade(param);
  const fragmentierung = getOmopFragmentierung(param);

  return (
    <>
      <Chip
        label={getOMOPStatusLabel(param.openehrStatus, t)}
        size="small"
        color={getOMOPStatusColor(param.openehrStatus)}
      />
      {/* Aufbau wie in der OMOP-Zelle: Klarname oben, Bezeichner darunter --
          dort Konzeptname und Konzept-ID, hier Archetypname und
          Archetypbezeichner. Kein Rueckfall auf den lokalen Namen (#246, hier
          fuer die zweite Darstellung): fehlt beides, steht die Leerstelle da. */}
      {archetyp ? (
        <>
          {param.openehrName && (
            <Typography variant="caption" display="block" color="textSecondary">
              {param.openehrName}
            </Typography>
          )}
          {param.openehrCode && (
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {param.openehrCode}
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="caption" display="block" color="text.disabled" fontStyle="italic">
          {t('dataCatalog.openehr.keinArchetyp')}
        </Typography>
      )}
      {pfade.length > 0 && (
        <Typography
          variant="caption"
          display="block"
          color="textSecondary"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          {pfade[0]}
          {pfade.length > 1 ? ` (+${pfade.length - 1})` : ''}
        </Typography>
      )}
      {abbildbarkeit === 'not_mappable' && (
        <Chip
          label={t('dataCatalog.openehr.nichtInOmopAbbildbar')}
          size="small"
          color="warning"
          sx={{ mt: 0.5, display: 'block', width: 'fit-content' }}
        />
      )}
      {abbildbarkeit === 'unassessed' && (
        <Typography variant="caption" display="block" color="text.disabled" fontStyle="italic">
          {t('dataCatalog.openehr.abbildbarkeitUnbekannt')}
        </Typography>
      )}
      {fragmentierung && (
        <Chip
          label={t('dataCatalog.openehr.fragmentierung', { anzahl: fragmentierung })}
          size="small"
          variant="outlined"
          sx={{ mt: 0.5, display: 'block', width: 'fit-content' }}
        />
      )}
    </>
  );
};

const ParameterRow = ({
  param,
  nameView,
  isExpanded,
  onRowClick,
  onFavoriteToggle,
  activeWorkingList,
  parameterLists,
  getParameterListStatus,
  isParameterInAnyList,
  handleRemoveFromList,
  handleAddToList,
  handleOpenListDialog,
  allTagsWithStats,
  getIconComponent,
  getPrimaryDescription,
  subtlePulse,
}) => {
  const { t, i18n } = useTranslation();
  const [detailParam, setDetailParam] = useState(param.hasDetailData ? param : null);
  const [detailStatus, setDetailStatus] = useState(param.hasDetailData ? 'success' : 'idle');
  const [detailError, setDetailError] = useState(null);

  const getPrimaryName = (parameter) => getDefaultPrimaryName(parameter, nameView);
  const getSecondaryOmopName = (parameter) => getDefaultSecondaryName(parameter, nameView);

  useEffect(() => {
    if (param.hasDetailData) {
      setDetailParam(param);
      setDetailStatus('success');
      setDetailError(null);
    }
  }, [param]);

  useEffect(() => {
    let cancelled = false;

    async function loadDetail() {
      setDetailStatus('loading');
      setDetailError(null);

      // Nur die Lookup-Felder, nicht das ganze `param`-Objekt: dessen Referenz
      // wechselt bei jedem Render der Elternliste, ein Abhaengigkeits-Eintrag
      // darauf wuerde den Ladevorgang in eine Schleife schicken.
      const result = await getParameter({ id: param.id, parameterId: param.parameterId });
      if (cancelled) {
        return;
      }

      if (result.source === 'api' && result.parameter) {
        setDetailParam(result.parameter);
        setDetailStatus('success');
        return;
      }

      setDetailStatus('error');
      setDetailError(t('dataCatalog.status.detailLoadFailed'));
    }

    if (isExpanded && !param.hasDetailData && !(detailParam?.id === param.id && detailParam?.hasDetailData)) {
      loadDetail();
    }

    return () => {
      cancelled = true;
    };
    // `t` bewusst nicht in den Abhaengigkeiten (#255, Runde 2): er uebersetzt
    // nur die Fehlermeldung innerhalb, seine Identitaet darf den Detailabruf
    // nicht erneut ausloesen (in Tests aendert sie sich bei jedem Render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailParam, isExpanded, param.hasDetailData, param.id, param.parameterId]);

  const resolvedParam = detailParam || param;
  const expandedLoading = isExpanded && !resolvedParam.hasDetailData && detailStatus !== 'error';
  // Herkunft der Zuordnung (#257) -- null, wenn die API keine Angabe macht;
  // dann bleibt die Zeile wie bisher ohne Herkunft-Chip (AK5).
  const mappingProvenance = getMappingProvenance(resolvedParam);
  // Nur *erfasste* Einheiten zaehlen als "mehrere Einheiten" (#264): eine
  // Karte mit `['kg', null]` traegt eine bekannte Einheit und einen nicht
  // erfassten Anteil, keine zwei Auspraegungen. Ohne diesen Filter zaehlte
  // die Zeile "2 Einheiten" und die Tooltip-Liste haette mit "kg, " geendet.
  const erfassteEinheiten = (resolvedParam.units || []).filter((u) => u.unit != null);

  return (
    <React.Fragment key={param.id}>
      <TableRow
        hover
        onClick={() => onRowClick(param.id)}
        sx={{
          cursor: 'pointer',
          '& > *': { borderBottom: 'unset' }
        }}
      >
        {activeWorkingList.length === 1 && parameterLists && (() => {
          const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
          const inListA = listA && listA.parameterIds.includes(param.id);

          return (
            <TableCell align="center">
              {inListA ? (
                <Tooltip title={t('dataCatalog.actions.inListClickRemove', { list: listA?.name })}>
                  <Box
                    onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[0], e)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: '#2196f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#1976d2',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    A
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={t('dataCatalog.actions.addToListNamed', { list: listA?.name })}>
                  <Box
                    onClick={(e) => handleAddToList(param.id, activeWorkingList[0], e)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: '2px solid #2196f3',
                      backgroundColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#2196f3',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    A
                  </Box>
                </Tooltip>
              )}
            </TableCell>
          );
        })()}

        {activeWorkingList.length === 2 && parameterLists && (() => {
          const status = getParameterListStatus(param.id);
          const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
          const listB = parameterLists.find(l => l.id === activeWorkingList[1]);

          const inListA = status === 'list1Only' || status === 'both';
          const inListB = status === 'list2Only' || status === 'both';

          return (
            <>
              <TableCell align="center">
                {inListA ? (
                  <Tooltip title={t('dataCatalog.actions.inListAClickRemove', { list: listA?.name })}>
                    <Box
                      onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[0], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#2196f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#1976d2',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      A
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('dataCatalog.actions.addToListA', { list: listA?.name })}>
                    <Box
                      onClick={(e) => handleAddToList(param.id, activeWorkingList[0], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        border: '2px solid #2196f3',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#2196f3',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(33, 150, 243, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                    </Box>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell align="center">
                {inListB ? (
                  <Tooltip title={t('dataCatalog.actions.inListBClickRemove', { list: listB?.name })}>
                    <Box
                      onClick={(e) => handleRemoveFromList(param.id, activeWorkingList[1], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#388e3c',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                      B
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip title={t('dataCatalog.actions.addToListB', { list: listB?.name })}>
                    <Box
                      onClick={(e) => handleAddToList(param.id, activeWorkingList[1], e)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        border: '2px solid #4caf50',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#4caf50',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s'
                      }}
                    >
                    </Box>
                  </Tooltip>
                )}
              </TableCell>
            </>
          );
        })()}

        <TableCell>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(param.id);
            }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" display="block">
                  <strong>{t('dataCatalog.table.tooltips.local')}:</strong> {resolvedParam.name}
                </Typography>
                {resolvedParam.omopName && (
                  <Typography variant="caption" display="block">
                    <strong>{t('dataCatalog.table.tooltips.omop')}:</strong> {resolvedParam.omopName}
                  </Typography>
                )}
                {resolvedParam.omopCode && (
                  <Typography variant="caption" display="block">
                    <strong>{t('dataCatalog.table.tooltips.code')}:</strong> {resolvedParam.omopCode}
                  </Typography>
                )}
                {resolvedParam.omopVocabulary && (
                  <Typography variant="caption" display="block">
                    <strong>{t('dataCatalog.table.tooltips.vocabulary')}:</strong> {resolvedParam.omopVocabulary}
                  </Typography>
                )}
              </Box>
            }
            placement="right"
            arrow
          >
            <Box>
              {nameView === ANSICHT_ALLE ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="caption" color="textSecondary" fontWeight="bold" sx={{ minWidth: 60 }}>
                      🏥 {t('dataCatalog.table.tooltips.local')}:
                      </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {resolvedParam.name}
                    </Typography>
                  </Box>
                  {/* Auch ohne Zuordnung wird die Zeile gezeigt -- als Leerstelle,
                      nicht als leere Zeile (#246). Fehlt sie ganz, sieht der
                      Modus `both` aus, als gaebe es keine OMOP-Spalte. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="caption" color="primary" fontWeight="bold" sx={{ minWidth: 60 }}>
                      🔷 {t('dataCatalog.table.tooltips.omop')}:
                    </Typography>
                    {getSecondaryOmopName(resolvedParam) ? (
                      <Typography variant="body2" color="primary">
                        {getSecondaryOmopName(resolvedParam)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled" fontStyle="italic">
                        {t('dataCatalog.table.notMapped')}
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                /* **Kein Rueckfall auf den lokalen Namen (#246).** In der
                   OMOP-Ansicht liefert `getPrimaryName` null, wenn keine
                   Zuordnung existiert. Dann steht hier die Leerstelle -- und
                   darunter der lokale Name, damit die Zeile ueberhaupt
                   identifizierbar bleibt: 576 von 3900 Karten teilen sich ihren
                   Namen mit einer anderen, ohne diesen Zusatz waeren sie in der
                   Liste nicht auseinanderzuhalten. */
                getPrimaryName(resolvedParam) ? (
                  <Typography variant="body1" fontWeight="bold">
                    {getPrimaryName(resolvedParam)}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body1" fontWeight="bold" color="text.disabled" fontStyle="italic">
                      {t('dataCatalog.table.notMapped')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {t('dataCatalog.table.localNameHint', { name: resolvedParam.name })}
                    </Typography>
                  </>
                )
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* **Nur noch das Profiling-Datum (#243).** Hier stand bis heute
                    `Quelle: chartevents, Code 211 | Profiling vom
                    2026-08-30T09:27:39Z` -- ein beim Import auf deutsch
                    eingefrorener Satz mit ISO-Rohzeitstempel.

                    Die Tabelle steht ohnehin als Chip im Datenbank-Mapping, der
                    Code jetzt ebenfalls dort. Was bleibt, ist das Datum -- und
                    das in der Schreibweise der gewaehlten Sprache.

                    Bewusst in Kauf genommen: 390 von 576 gleichnamigen Karten
                    unterschieden sich in der Liste **nur** durch den Code. Sie
                    sind hier ab jetzt erst nach dem Aufklappen zu trennen. Der
                    bessere Unterscheider waere `CareVue` bzw. `Blood` -- das ist
                    #251, und dann gehoert er hierher. */}
                <Typography variant="caption" color="textSecondary">
                  {resolvedParam.profiledAt
                    ? t('dataCatalog.table.profiledAt', {
                        date: new Date(resolvedParam.profiledAt).toLocaleDateString(i18n.language),
                      })
                    : ''}
                </Typography>
                {erfassteEinheiten.length > 1 && (
                  <Tooltip
                    title={t('dataCatalog.table.tooltips.multipleUnits', {
                      units: erfassteEinheiten.map(u => u.unit).join(', '),
                    })}
                  >
                    <Chip
                      label={t('dataCatalog.table.tooltips.unitsCount', { count: erfassteEinheiten.length })}
                      size="small"
                      color="warning"
                      sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Tooltip>
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {/* `|| []` statt `resolvedParam.tags` blank (#255, CI-Fund Job 1377).
                In Produktion setzt `transformParameter` das Feld immer auf ein
                Array -- aber die Zeile darf nicht davon abhaengen, dass jeder
                Weg zu ihr durch diese eine Funktion fuehrt. Genau daran ist
                #226 gescheitert: ein Feld, das "immer da ist", war es an einer
                Stelle nicht, und der Absturz riss die ganze Zeile mit. */}
            {(resolvedParam.tags || []).map(tag => {
              const tagData = allTagsWithStats.find(t => t.name === tag);
              const isPrivate = tagData?.isPrivate || false;
              const CustomIconComponent = tagData?.icon ? getIconComponent(tagData.icon) : null;

              return (
                <Tooltip
                  key={tag}
                  title={tagData?.createdBy ? t('dataCatalog.table.tooltips.createdBy', { user: tagData.createdBy }) : ''}
                >
                  <Chip
                    icon={
                      CustomIconComponent ? (
                        <CustomIconComponent fontSize="small" />
                      ) : isPrivate ? (
                        <LockIcon fontSize="small" />
                      ) : (
                        <PublicIcon fontSize="small" />
                      )
                    }
                    label={tag}
                    size="small"
                    color={isPrivate ? 'secondary' : 'default'}
                    variant="outlined"
                  />
                </Tooltip>
              );
            })}
            {resolvedParam.sourceSystem && (
              <Chip
                icon={<StorageIcon />}
                label={resolvedParam.sourceSystem}
                size="small"
                color="info"
                variant="filled"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        </TableCell>

        <TableCell>
          <FrequencyCell
            caseCoverage={resolvedParam.frequencyMetrics?.caseCoverage ?? resolvedParam.frequency}
            stationCount={resolvedParam.frequencyMetrics?.stationCount ?? resolvedParam.stationCount}
            suppressed={resolvedParam.frequencyMetrics?.suppressed}
            suppressionThreshold={resolvedParam.frequencyMetrics?.suppressionThreshold}
          />
        </TableCell>

        {zeigtOmop(nameView) && (
          <TableCell>
            <Chip
              label={getOMOPStatusLabel(resolvedParam.omopStatus, t)}
              size="small"
              color={getOMOPStatusColor(resolvedParam.omopStatus)}
            />
            {resolvedParam.omopCode && (
              <Typography variant="caption" display="block" color="textSecondary">
                {resolvedParam.omopCode}
              </Typography>
            )}
            {/* Herkunft: importiert / KI-Vorschlag / vom Menschen validiert
                (#257 AK1). Der KI-Vorschlag bleibt "outlined" -- er darf nicht
                wie eine geprueft aussehende Zuordnung wirken (AK3). */}
            {mappingProvenance && (
              <Chip
                label={getMappingProvenanceLabel(mappingProvenance, t)}
                size="small"
                color={getMappingProvenanceColor(mappingProvenance)}
                variant={mappingProvenance === OMOP_MAPPING_SOURCE_AI ? 'outlined' : 'filled'}
                sx={{ mt: 0.5, display: 'block', width: 'fit-content' }}
              />
            )}
          </TableCell>
        )}

        {zeigtOpenEhr(nameView) && (
          <TableCell>
            <OpenEhrZelle param={resolvedParam} t={t} />
          </TableCell>
        )}

        <TableCell>
          <SensitivityBadge level={resolvedParam.sensitivity} />
        </TableCell>

        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            {/* `param.isFavorite`, nicht `resolvedParam.isFavorite` (#255, Runde
                2): sobald eine Zeile einmal aufgeklappt war, haengt
                `detailParam` an der Detailantwort und wird nie wieder aus dem
                `param`-Prop aufgefrischt (Zeile 75-81 aktualisiert ihn nur,
                wenn `param.hasDetailData` schon beim Laden true war). Die
                Detailantwort traegt aber noch das alte API-Feld
                `displayData.isFavorite`, nicht den Arbeitslisten-Stand. Ohne
                diese Trennung zeigt eine aufgeklappte Zeile dauerhaft die
                falsche Quelle und reagiert nicht mehr auf Favoriten-Klicks. */}
            <FavoriteButton
              isFavorite={param.isFavorite}
              onClick={() => onFavoriteToggle(param.id)}
            />
            {/* Oeffnet immer den Listen-Dialog (#265) -- fuer die aktive
                Arbeitsliste ist die A/B-Spalte oben der Umschalter (rein/raus).
                Wuerde dieser Knopf zusaetzlich quick-adden, gaebe es wieder
                zwei Bedienelemente fuer dieselbe Liste in derselben Zeile. */}
            <ListButton
              isInList={isParameterInAnyList(resolvedParam.id)}
              onClick={(e) => handleOpenListDialog(resolvedParam.id, e)}
            />
            <IconButton
              size="small"
              component={Link}
              to={`/parameter/${getParameterLookupId(resolvedParam)}`}
              onClick={(e) => e.stopPropagation()}
              title={t('dataCatalog.table.tooltips.viewAllDetails')}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        {/* Die aufgeklappte Zeile muss ueber alle Spalten reichen -- sonst
            schiebt sie die Tabelle sichtbar zusammen. Seit #782 koennen es
            **zwei** Zuordnungsspalten sein (Stellung "alle"). */}
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={
            6 +
            (zeigtOmop(nameView) ? 1 : 0) +
            (zeigtOpenEhr(nameView) ? 1 : 0) +
            (activeWorkingList.length > 0 ? 1 : 0)
          }
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ExpandedRow
              param={resolvedParam}
              parameterLists={parameterLists}
              activeWorkingList={activeWorkingList}
              handleOpenListDialog={handleOpenListDialog}
              loading={expandedLoading}
              error={detailError}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default ParameterRow;
